import React, { memo } from 'react';
import { Typography, Box } from '@material-ui/core';
import { useFormikContext } from 'formik';
import type { FormField } from '../interfaces';
import { FormikSlider } from './FormikSlider';
import { UnsupportedFieldTypeFallback } from './FallbackFormField';
import { FormikRadioGroup } from './FormikRadioGroup';
import { FormDropdown } from './FormFields/FormDropdown';
import { BudsjettertSelector } from './BudsjettertSelector';
import { EiendomSelector } from './EiendomSelector';
import {
    getLocalizedLabel,
    findCamundaPropertyValue,
    parseTranslatedJsonArrayOfStrings,
    formfieldIsRequired,
    formfieldIsReadOnly,
} from '../utils';
import { Vurderingsskjema } from '@klp/prosjekt/components/Vurderingsskjema/Vurderingsskjema';
import { GarantiMottattSelector } from './GarantiMottattSelector';
import { CalculatedSelector } from './CalculatedSelector';
import { LinkSelector } from './LinkSelector';
import { VurderingsskjemaSelector } from './VurderingsskjemaSelector';
import { FormikNumericFormat } from './FormikNumericFormat';
import { NyProsjektlederSelector } from './NyProsjektlederSelector';
import { FormikCheckboxGroup } from './FormikCheckboxGroup';
import { ConditionalAssignSelector } from './ConditionalAssignSelector';
import { ConditionalLinkSelector } from './ConditionalLinkSelector';
import { TextArea } from './FormFields/TextArea';
import { BasicTextField } from './FormFields/BasicTextField';
import { DateField } from './FormFields/DateField';
import { EnumSelect } from './FormFields/EnumSelect';
import { LeverandorListSelector } from './LeverandorListSelector';
import { IdeBudsjettertSelector } from './IdeBudsjettertSelector';
import { VedleggListSelector } from './VedleggListSelector';
import { BudsjettinfoSelector } from './BudsjettinfoSelector';
import { BudsjettnummerSelector } from './BudsjettnummerSelector';
import { CompanySelector } from './CompanySelector/CompanySelector';
import { Alert } from '@material-ui/lab';
import type { ProjectTypeStatus } from '@klp/prosjekt/interfaces';
import { CalculatedSelectorEditable } from './CalculatedSelectorEditable';
import { BudsjettinfoMultipleSelector } from './BudsjettinfoMultipleSelector';
import { LinkSelectorEditable } from './LinkSelectorEditable';
import { KostnadsKalkyle } from '@klp/prosjekt/components/kostnadskalkyle/KostnadsKalkyle';
import { AvfallDokumentSelector } from './AvfallDokumentSelector';
import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import { GarantiDokumentUpload } from './GarantiDokumentUpload';
import { GarantiDokumentSelect } from './GarantiDokumentSelect';
import type { LEVERANDOR_REGIONS } from '@klp/leverandor/types';
import { getFormFieldStyles } from '../formFieldStyles';
import { BransjeSelector } from './BransjeSelector';
import { RisikovurderingSelector } from '@klp/leverandor/components/RisikovurderingSelector';
import { KlassifiseringsSelector } from './KlassifiseringSelector';
import { VedleggSelector } from './VedleggSelector';
import { useFormContext } from '../context/FormContext';
import { getConfig } from '@klpkonsern/appconfig';
import { Link } from '@klp/design/Link/Link';
import { SamletRisikoSelector } from './SamletRisikoSelector';

type FormFieldProps = {
    containerModule: 'prosjekt' | 'leverandor' | 'kontraktsforvaltning';
    prosessInstansId: string;
    prosjektId?: string;
    superofficeContactId?: string;
    organisasjonsnummer?: string;
    field: FormField;
    fields: Array<FormField>;
    prosjektTypeStatus?: ProjectTypeStatus;
    formIsDisabled: boolean;
    locale: string;
    hiddenField?: boolean;
};

const FormFieldComponent = ({
    field,
    fields,
    prosjektTypeStatus,
    prosessInstansId,
    prosjektId,
    formIsDisabled,
    locale,
    hiddenField,
    containerModule,
    superofficeContactId,
    organisasjonsnummer,
}: FormFieldProps) => {
    const classes = getFormFieldStyles();
    const { id, type, label, defaultValue, properties, value } = field;
    const { values } = useFormikContext();
    const fieldValue = (values as Record<string, unknown>)[id as string];
    const { regionContext } = useFormContext();

    // Properties
    const subtype = findCamundaPropertyValue('type', properties);
    const apiProperty = findCamundaPropertyValue('api', properties);
    const dropdownItems = findCamundaPropertyValue('items', properties) ?? '[]';

    const parsedDropdownItems = parseTranslatedJsonArrayOfStrings(
        dropdownItems,
        locale,
    );

    // Validation
    const isReadOnly =
        formIsDisabled || formfieldIsReadOnly(field, regionContext);

    // Assuming 'enumValues' comes as a property for 'Enum' type
    const enumValues =
        properties
            ?.find((prop) => prop.id === 'enumValues')
            ?.value?.split(',') || [];

    const percentageMarks = [
        { value: 0, label: '0%' },
        { value: 100, label: '100%' },
    ];

    const defaultPercentage = defaultValue
        ? parseInt(defaultValue)
        : value
          ? parseInt(value)
          : 0;

    const isRequired = Boolean(
        !hiddenField && formfieldIsRequired(field, regionContext),
    );

    const renderField = () => {
        switch (type) {
            case 'string':
                switch (subtype) {
                    case CamundaFields.MAIN_TITLE:
                        switch (containerModule) {
                            case 'prosjekt':
                                return (
                                    <>
                                        <Typography variant="h1">
                                            {`${getLocalizedLabel(
                                                `project.formFields.${id}`,
                                                label,
                                                id,
                                            )} - ${prosjektTypeStatus?.prosjektskjemaType}`}
                                        </Typography>
                                        <Typography variant="h2">
                                            {`${prosjektTypeStatus?.typeNavn} - ${prosjektTypeStatus?.prosjektStatus}`}
                                        </Typography>
                                        <Typography variant="h3">
                                            {prosjektTypeStatus?.prosjektNavn ??
                                                ' - '}
                                        </Typography>
                                    </>
                                );
                            default:
                                return (
                                    <Typography variant="h1">
                                        {`${getLocalizedLabel(
                                            `project.formFields.${id}`,
                                            label,
                                            id,
                                        )}`}
                                    </Typography>
                                );
                        }

                    case CamundaFields.H2_TITLE:
                        return (
                            <>
                                <Typography
                                    variant="h2"
                                    style={{
                                        paddingTop: '20px',
                                    }}
                                >
                                    {getLocalizedLabel(
                                        `project.formFields.${id}`,
                                        label,
                                        id,
                                    )}
                                </Typography>{' '}
                            </>
                        );
                    case CamundaFields.H3_TITLE:
                        return (
                            <>
                                <Typography variant="h3">
                                    {getLocalizedLabel(
                                        `project.formFields.${id}`,
                                        label,
                                        id,
                                    )}
                                </Typography>
                            </>
                        );
                    case CamundaFields.TEXT:
                        return (
                            <Typography variant="body1">
                                {getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                            </Typography>
                        );
                    case CamundaFields.ALERT_INFO:
                        return (
                            <Alert severity="info">
                                {getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                            </Alert>
                        );
                    case CamundaFields.ALERT_WARNING:
                        return (
                            <Alert severity="warning">
                                {getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                            </Alert>
                        );
                    case CamundaFields.LEVERANDOR_DEVIATION_ALERT:
                        return (
                            <Alert severity="warning">
                                <Typography>
                                    {getLocalizedLabel(
                                        `project.formFields.${id}`,
                                        label,
                                        id,
                                    )}
                                </Typography>
                                <Link
                                    target="_blank"
                                    href={getConfig(
                                        'WEBAPP_APP_AVVIKSSYSTEM_URL',
                                    )}
                                    translation="leverandor.deviationSystemLink"
                                />
                            </Alert>
                        );

                    case CamundaFields.LINK_EDITABLE:
                        return (
                            <LinkSelectorEditable
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                field={field}
                            />
                        );
                    case CamundaFields.LINK:
                        return (
                            <LinkSelector
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                field={field}
                            />
                        );

                    case CamundaFields.RADIOBUTTON:
                        return (
                            <FormikRadioGroup
                                id={id}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                field={field}
                                locale={locale}
                            />
                        );

                    case CamundaFields.CHECKBOX:
                        return (
                            <FormikCheckboxGroup
                                field={field}
                                id={id}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                locale={locale}
                            />
                        );

                    case CamundaFields.DROPDOWN:
                        return (
                            <FormDropdown
                                id={id}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                apiProperty={apiProperty}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                classes={classes}
                                defaultValue={value ?? defaultValue}
                                fallbackItems={parsedDropdownItems}
                                isHidden={hiddenField}
                            />
                        );

                    case CamundaFields.TEXTAREA:
                        return (
                            <TextArea
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                classes={classes}
                            />
                        );
                    case CamundaFields.CONDITIONAL_DROPDOWN:
                        return (
                            <ConditionalAssignSelector
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                field={field}
                            />
                        );
                    case CamundaFields.BRONNOYSUND:
                        return (
                            <CompanySelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.EIENDOMSNAVN:
                        return (
                            <EiendomSelector
                                isReadOnly={isReadOnly}
                                field={field}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.BRANSJE:
                        return (
                            <BransjeSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.LEVERANDOR_KLASSIFISERING:
                        return (
                            <KlassifiseringsSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.BUDSJETTNUMMER:
                        return (
                            <BudsjettnummerSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.BUDSJETTINFO_MULTIPLE:
                        return (
                            <BudsjettinfoMultipleSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                            />
                        );

                    case CamundaFields.BUDSJETTINFO:
                        return (
                            <BudsjettinfoSelector
                                field={field}
                                fields={fields}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                isHidden={hiddenField}
                            />
                        );

                    case CamundaFields.VURDERINGSSKJEMA:
                        return (
                            <Vurderingsskjema
                                prosessInstansId={prosessInstansId}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                field={field}
                            />
                        );
                    case CamundaFields.VURDERINGSSKJEMA_OPPSUMMERING:
                        return (
                            <VurderingsskjemaSelector
                                field={field}
                                prosessInstansId={prosessInstansId}
                                defaultPercentage={defaultPercentage}
                                percentageMarks={percentageMarks}
                                isReadOnly={true}
                                isRequired={isRequired}
                            />
                        );

                    case CamundaFields.CONDITIONAL:
                        return <React.Fragment />;
                    case CamundaFields.LEVERANDOR_LISTE:
                        return (
                            <LeverandorListSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                prosessInstansId={prosessInstansId}
                            />
                        );
                    case CamundaFields.VEDLEGG_SINGLE:
                        return (
                            <VedleggSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                prosessInstansId={prosessInstansId}
                                attachmentType={undefined}
                            />
                        );
                    case CamundaFields.VEDLEGG_LISTE:
                        return (
                            <VedleggListSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                prosessInstansId={prosessInstansId}
                            />
                        );
                    case CamundaFields.AVFALL_DOKUMENT:
                        return (
                            <AvfallDokumentSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                prosessInstansId={prosessInstansId}
                            />
                        );
                    case CamundaFields.GARANTI_DOKUMENT_UPLOAD:
                        return (
                            <GarantiDokumentUpload
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                prosessInstansId={prosessInstansId}
                            />
                        );
                    case CamundaFields.GARANTI_DOKUMENT_SELECT:
                        return (
                            <GarantiDokumentSelect
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.KOSTNADSKALKYLE:
                        if (!prosjektId) return null;
                        return (
                            <KostnadsKalkyle
                                field={field}
                                prosjektId={prosjektId}
                                prosessInstansId={prosessInstansId}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                prosjektNavn={`${prosjektTypeStatus?.typeNavn} - ${prosjektTypeStatus?.prosjektNavn} - ${prosjektTypeStatus?.prosjektStatus}`}
                            />
                        );
                    case CamundaFields.CALCULATED_EDITABLE:
                        return (
                            <CalculatedSelectorEditable
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.RISIKOVURDERING:
                        if (!organisasjonsnummer || !superofficeContactId) {
                            return null;
                        }
                        return (
                            <RisikovurderingSelector
                                field={field}
                                isReadOnly={isReadOnly}
                                orgnummer={organisasjonsnummer}
                                superofficeId={superofficeContactId}
                                region={regionContext as LEVERANDOR_REGIONS}
                            />
                        );
                    case CamundaFields.TILDEL_KOMMENTARER:
                        // This is a custom field type for general project comments
                        return null;
                    case CamundaFields.ROLLE_KOMMENTARER:
                        return null;
                    default:
                        return (
                            <BasicTextField
                                field={field}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                classes={classes}
                            />
                        );
                }
            case 'long':
                switch (subtype) {
                    case CamundaFields.PERCENTSLIDER:
                        return (
                            <FormikSlider
                                field={field}
                                defaultPercentage={defaultPercentage}
                                percentageMarks={percentageMarks}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                            />
                        );
                    case CamundaFields.PERCENTFIELD:
                        return (
                            <FormikNumericFormat
                                name={id}
                                value={fieldValue as number}
                                variant="outlined"
                                id={id}
                                allowNegative={false}
                                isAllowed={({ floatValue }) =>
                                    Boolean(!floatValue || floatValue <= 100)
                                }
                                decimalScale={
                                    Number(
                                        findCamundaPropertyValue(
                                            'decimalScale',
                                            properties,
                                        ),
                                    ) || 2
                                }
                                placeholder={
                                    findCamundaPropertyValue(
                                        'placeholder',
                                        properties,
                                    ) || ''
                                }
                                defaultValue={value ?? defaultValue}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                            />
                        );

                    case CamundaFields.CALCULATED:
                        return (
                            <CalculatedSelector
                                field={field}
                                isReadOnly={true}
                                isRequired={isRequired}
                            />
                        );
                    case CamundaFields.KOSTNADSKALKYLE:
                        if (!prosjektId) return null;
                        return (
                            <KostnadsKalkyle
                                field={field}
                                prosjektId={prosjektId}
                                prosessInstansId={prosessInstansId}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                prosjektNavn={`${prosjektTypeStatus?.typeNavn} - ${prosjektTypeStatus?.prosjektNavn} - ${prosjektTypeStatus?.prosjektStatus}`}
                            />
                        );
                    case CamundaFields.VURDERINGSSKJEMA:
                        return (
                            <Vurderingsskjema
                                prosessInstansId={prosessInstansId}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                field={field}
                            />
                        );

                    case CamundaFields.VURDERINGSSKJEMA_OPPSUMMERING:
                        return (
                            <VurderingsskjemaSelector
                                field={field}
                                prosessInstansId={prosessInstansId}
                                defaultPercentage={defaultPercentage}
                                percentageMarks={percentageMarks}
                                isReadOnly={true}
                                isRequired={isRequired}
                            />
                        );

                    case CamundaFields.RADIOBUTTON:
                        return (
                            <FormikRadioGroup
                                id={id}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                field={field}
                                locale={locale}
                            />
                        );

                    case CamundaFields.CHECKBOX:
                        return (
                            <FormikCheckboxGroup
                                field={field}
                                id={id}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                locale={locale}
                            />
                        );

                    case CamundaFields.DROPDOWN:
                        return (
                            <FormDropdown
                                id={id}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                apiProperty={apiProperty}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                classes={classes}
                                defaultValue={value ?? defaultValue}
                                fallbackItems={parsedDropdownItems}
                                isHidden={hiddenField}
                            />
                        );

                    case CamundaFields.CONDITIONAL:
                        return <React.Fragment />;

                    case CamundaFields.RISK_CALCULATED:
                        return (
                            <SamletRisikoSelector
                                field={field}
                                isReadOnly={true}
                                isRequired={isRequired}
                            />
                        );

                    default:
                        return (
                            <FormikNumericFormat
                                name={id}
                                value={fieldValue as number}
                                variant="outlined"
                                id={id}
                                placeholder={
                                    findCamundaPropertyValue(
                                        'placeholder',
                                        properties,
                                    ) || ''
                                }
                                defaultValue={value ?? defaultValue}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                            />
                        );
                }
            case 'date':
                return (
                    <DateField
                        field={field}
                        isReadOnly={isReadOnly}
                        isRequired={isRequired}
                        classes={classes}
                    />
                );
            case 'boolean':
                switch (subtype) {
                    case CamundaFields.CONSTANT:
                        return <React.Fragment />;
                    case CamundaFields.CONDITIONAL:
                        return <React.Fragment />;
                    case CamundaFields.CHECKBOX:
                        return (
                            <FormikCheckboxGroup
                                field={field}
                                id={id}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                locale={locale}
                            />
                        );
                    case CamundaFields.BUDSJETTERT:
                        return (
                            <BudsjettertSelector
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                field={field}
                                fields={fields}
                                locale={locale}
                            />
                        );
                    case CamundaFields.IDE_BUDSJETTERT:
                        return (
                            <IdeBudsjettertSelector
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                field={field}
                                fields={fields}
                                locale={locale}
                            />
                        );
                    case CamundaFields.GARANTI_MOTTATT:
                        return (
                            <GarantiMottattSelector
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                field={field}
                                fields={fields}
                                locale={locale}
                            />
                        );

                    case CamundaFields.PROSJEKTLEDER_BEKREFTELSE:
                        return (
                            <NyProsjektlederSelector
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                field={field}
                                fields={fields}
                                locale={locale}
                            />
                        );
                    case CamundaFields.CONDITIONAL_LINK_SELECTOR:
                        return (
                            <ConditionalLinkSelector
                                conditionalFieldId={
                                    findCamundaPropertyValue(
                                        'conditionalField',
                                        properties,
                                    ) || ''
                                }
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                field={field}
                                fields={fields}
                                locale={locale}
                            />
                        );
                    default:
                        return (
                            <FormikRadioGroup
                                field={field}
                                id={id}
                                isRequired={isRequired}
                                isReadOnly={isReadOnly}
                                label={getLocalizedLabel(
                                    `project.formFields.${id}`,
                                    label,
                                    id,
                                )}
                                locale={locale}
                            />
                        );
                }
            case 'enum':
                return (
                    <EnumSelect
                        field={field}
                        isReadOnly={isReadOnly}
                        isRequired={isRequired}
                        enumValues={enumValues}
                        defaultValue={defaultValue}
                        value={value}
                    />
                );
            default:
                return <UnsupportedFieldTypeFallback />;
        }
    };

    return (
        <Box
            className={
                hiddenField ? classes.hiddenField : classes.formFieldContainer
            }
        >
            {renderField()}
        </Box>
    );
};

export const CustomFormField = memo(FormFieldComponent);
